import React from "react";
import "../DashboardStyles.css";

export default function Chapter4Dashboard() {
  return (
    <div className="chapter chapter4">
      <div className="chapter-content">
        <p className="chapter-eyebrow">Chapter 4</p>
        <h2>Infrastructure</h2>

        <h3>4.1 Introduction</h3>
        <p>
          Infrastructure is the physical and digital foundation upon which
          economic activity is built. This chapter assesses the current state of
          the Nebraska Panhandle’s key infrastructure categories:
          transportation, broadband and telecommunications, water and
          wastewater, energy distribution, and healthcare facilities. For each
          category, the chapter describes existing conditions, identifies key
          gaps and challenges, and notes planned investments that will shape the
          region’s infrastructure landscape over the CEDS planning horizon.
        </p>

        <h3>4.2 Transportation Infrastructure</h3>
        <p>
          The Panhandle’s highway network is anchored by US Highway 26, which
          carries east-west traffic through the North Platte River valley; US
          Highway 385, a major north-south spine; US Highway 20, which links
          Chadron and northern Panhandle communities eastward; and Interstate 80
          across the southern Panhandle. Nebraska’s FY 2026–2029 Statewide
          Transportation Improvement Program identifies several major regional
          investments, including the BNSF Bridgeport viaduct, a $16.163 million
          US-26 grade-separation project at the BNSF crossing on the west edge of
          Bridgeport. The STIP also programs major US-26 corridor improvements
          between Minatare and US-385 in Scotts Bluff County, including a
          $21.312 million Phase 1 project for new westbound lanes from US-385 to
          County Road 89, a new bridge, and culvert extensions, followed by a
          $60.820 million Phase 2 project for new lanes, grading, culvert
          extensions, and resurfacing of existing lanes. Together, these two
          phases represent approximately $82.1 million in programmed investment.
        </p>
        <p>
          Based on FHWA’s 2025 National Bridge Inventory county table excluding
          federal bridges, the eleven-county Panhandle contains 940 bridges. Of
          these, 689 are rated Good, 223 are rated Fair, and 28 are rated Poor,
          meaning 73.3% are Good, 23.7% are Fair, and 3.0% are Poor. This
          regional poor-bridge rate is less than half Nebraska’s statewide
          non-federal poor-bridge rate of approximately 7.5%. Dawes County
          presents the region’s most elevated bridge-condition concern, with 10
          Poor bridges out of 89 total, or 11.2%, significant given the county’s
          role as a northern Panhandle gateway and recreation corridor. Scotts
          Bluff County also has 10 Poor bridges, though its larger bridge
          inventory results in a lower poor-bridge rate of 4.0%.
        </p>
        <p>
          Commercial air service in the Panhandle is more distributed than a
          single-airport model suggests. Western Nebraska Regional Airport (BFF)
          in Scottsbluff provides daily United Airlines service operated by
          SkyWest to Denver and beyond. Alliance Municipal Airport (AIA) provides
          Denver Air Connection service from Alliance to Denver, and Chadron
          Municipal Airport (CDR) provides Denver Air Connection commercial
          service to Denver International Airport. Together, these airports
          connect the Panhandle to the national commercial air service network,
          while additional public-use and general aviation airports support
          local access, emergency response, business travel, agricultural
          aviation, and recreational flying across the region.
        </p>

        <h3>4.3 Broadband and Telecommunications Infrastructure</h3>
        <figure className="ceds-figure ceds-figure--portrait">
          <img
            src="/images/ceds/fixed-wireless-tower.jpg"
            alt="Communications tower with fixed wireless antennas beside a gravel path"
            loading="lazy"
          />
          <figcaption>
            Fixed wireless infrastructure serving the Panhandle. Objective 3.2
            tracks terrestrial broadband availability separately from
            satellite, so that progress reflects infrastructure built rather
            than coverage reclassified.
          </figcaption>
        </figure>
        <p>
          Broadband connectivity is one of the most critical and most unevenly
          distributed infrastructure resources in the Nebraska Panhandle.
          According to the Nebraska Broadband Office’s BEAD Five-Year Action Plan
          (2023), approximately 12 to 15 percent of Nebraska’s
          broadband-serviceable locations are unserved or underserved, and the
          Panhandle’s frontier and rural counties represent a
          disproportionately high share. Nebraska was originally allocated
          $405.3 million in BEAD funding. Under the Trump administration’s
          Benefit of the Bargain initiative, the state’s final approved proposal
          committed approximately $44.5 million — roughly 11 percent of the
          original allocation — to broadband deployment, targeting 14,032
          BEAD-eligible locations statewide. Seven ISPs signed subgrant
          agreements covering approximately 88 percent of those locations. Three
          providers declined to finalize their awards, leaving 1,735 locations —
          12 percent of eligible — without a committed provider as of June 2026.
          Nebraska has since reopened its BEAD bidding portal to fill those
          gaps. More than $340 million in unallocated BEAD funds remains subject
          to pending NTIA guidance on permissible non-deployment uses, and the
          Nebraska Broadband Office is operating without a permanent director.
          The Panhandle’s frontier and rural counties (which represent a
          disproportionate share of unserved and underserved locations) face
          continued uncertainty about deployment timelines and technology mix,
          with most remaining locations slated for satellite or fixed wireless
          service rather than fiber.
        </p>
        <p className="source-note">
          Source note: Nebraska Broadband Office, NBO Subgrant Agreements press
          release, May 22, 2026 (dot.nebraska.gov); Light Reading, “Nebraska to
          reopen BEAD bids after providers drop out,” May 26, 2026; Nebraska
          Public Media / Erin Bamer, “Future of Nebraska Broadband Office
          unclear following reduction in federal BEAD funds,” May 4, 2026.
        </p>

        <div className="callout">
          <span className="callout-label">Pair this with the survey finding</span>
          <p>
            Chapter 11 shows residents rank broadband <strong>last</strong>{" "}
            among community concerns (19%). That does not diminish the case for
            BEAD — it reframes it: broadband is enabling infrastructure for
            telehealth, precision agriculture, and remote work (the
            opportunities in Chapter 5), more than a stand-alone resident
            demand. Framing it this way in grant narratives keeps the plan
            honest and still fundable.
          </p>
        </div>

        <h3>4.4 Water and Wastewater Infrastructure</h3>
        <p>
          The Panhandle’s municipal water systems range from the relatively
          well-developed systems serving Scottsbluff, Gering, Alliance, and
          Chadron to the small, aging systems serving the region’s many
          unincorporated communities and rural villages. Many of these smaller
          systems were built decades ago and face ongoing challenges related to
          aging infrastructure, limited rate bases, and difficulty attracting
          and retaining qualified operators. Wastewater treatment infrastructure
          presents particular challenges for smaller communities where upgrading
          systems to comply with modern environmental regulations requires
          capital investment that is often disproportionately burdensome.
        </p>
        <figure className="ceds-figure ceds-figure--portrait">
          <img
            src="/images/ceds/morrill-wastewater-2026.png"
            alt="Trench with a large concrete wastewater pipe being replaced, Morrill County"
            loading="lazy"
          />
          <figcaption>
            Wastewater line replacement, Morrill County, 2026. Much of the
            region’s water and wastewater infrastructure predates 1960, and
            system capacity determines where new housing can physically be
            built.
          </figcaption>
        </figure>

        <h3>4.5 Energy Distribution Infrastructure</h3>
        <p>
          The Nebraska Panhandle is served by a network of public power
          utilities consistent with Nebraska’s unique status as the only state
          in the nation where all electricity is provided by publicly owned
          utilities. The major electric utilities serving the region are the
          Nebraska Public Power District (NPPD), which provides wholesale and
          retail service across much of the Panhandle, and the Panhandle Rural
          Electric Membership Association (PREMA), a distribution cooperative
          serving rural portions of the region. Both utilities provide reliable
          in-region power service to residents, agricultural operations, and
          businesses.
        </p>

        <h4>The Transmission Constraint</h4>
        <p>
          The existing grid infrastructure was designed to serve in-region load
          — residential, commercial, and agricultural consumption — not to
          export large volumes of new renewable generation to regional or
          interstate markets. This distinction matters because the Panhandle’s
          wind and solar resources, while among the strongest in the state,
          cannot be commercially developed at utility scale without transmission
          capacity sufficient to move power to buyers elsewhere in the regional
          grid. The Southwest Power Pool (SPP), which operates the regional
          transmission system, manages interconnection queues that can impose
          years-long timelines and significant cost on new generation projects
          seeking to connect.
        </p>
        <p>
          This transmission gap is not unique to the Panhandle — it is the
          defining infrastructure constraint for renewable energy development
          across the rural Great Plains — but it bears directly on the economic
          development opportunities described in Chapter 5. Counties in the
          southern Panhandle where wind and solar resources are strongest
          (Cheyenne, Kimball, Deuel, and Garden) are also counties where
          population decline and thin property-tax bases make new revenue from
          lease payments and wind-energy property taxes economically
          significant. Federal transmission investment programs under the
          Inflation Reduction Act and the Infrastructure Investment and Jobs Act
          represent the most viable path to resolving this constraint at the
          scale needed for utility-scale development.
        </p>

        <h4>Infrastructure Gaps Summary — Energy</h4>
        <p>
          The Panhandle’s energy infrastructure presents a split picture.
          Conventional power distribution is reliable and well-managed by NPPD
          and PREMA. The gap is transmission export capacity. Until transmission
          is expanded, the region’s renewable energy assets — Chapter 3
          documents their exceptional quality — cannot be converted into
          economic development at scale. PADD’s role in this arena is
          coordination and advocacy: supporting county commissioners and
          landowner groups in transmission planning processes, engaging NPPD and
          SPP on regional capacity studies, and ensuring Panhandle communities
          are positioned to benefit from any federal transmission investment
          directed to the region.
        </p>

        <h3>4.6 Healthcare Facilities Infrastructure</h3>
        <p>
          Regional West Medical Center in Scottsbluff is the Panhandle’s primary
          healthcare facility and one of the region’s largest employers. As a
          Level II Trauma Center and the only facility in the region capable of
          providing complex surgical, specialty, and critical care services,
          Regional West serves patients from across the eleven-county Panhandle
          and from neighboring areas of Wyoming and Colorado. Critical Access
          Hospitals operating in the region include Box Butte General Hospital
          (Alliance), Gordon Memorial Health Services (Gordon), Morrill County
          Community Hospital (Bridgeport), Kimball Health Services (Kimball),
          Cheyenne County Hospital (Sidney), and Chadron Community Hospital
          (Chadron) — each both a healthcare anchor and an economic anchor,
          typically ranking among the top three employers in its county.
        </p>
        <p>
          Multiple Panhandle counties carry Health Professional Shortage Area
          (HPSA) designations for primary care, mental health, and/or dental
          care. Sioux, Banner, Garden, Sheridan, and Morrill counties have
          historically carried whole-county primary care HPSA designations, and
          mental health HPSAs are broadly designated across the region. These
          designations create eligibility for National Health Service Corps loan
          repayment, J-1 visa waiver opportunities, and enhanced Medicaid
          reimbursement.
        </p>

        <h3>4.7 Housing Infrastructure</h3>
        <p>
          Inadequate housing supply is one of the most commonly cited barriers
          to workforce attraction and retention in the Panhandle. Employers
          across multiple sectors — healthcare, agriculture, retail, and
          services — report difficulty recruiting workers in part because of
          limited housing availability. The Panhandle’s housing stock is aging,
          with a significant share of units built before 1980. Addressing
          housing infrastructure needs across the full spectrum — from new
          construction in growth areas to rehabilitation and demolition of
          blighted units in declining communities — requires a differentiated
          strategy tailored to local conditions.
        </p>

        <h3>4.8 Infrastructure Gaps Summary</h3>
        <ul className="ceds-list">
          <li>
            <strong>Transportation:</strong> The highway network is receiving
            meaningful STIP investment; county road and bridge networks remain
            underfunded; BFF air service is a critical asset to protect and
            expand.
          </li>
          <li>
            <strong>Broadband:</strong> Nebraska’s BEAD program is underway but
            significantly constrained. Only $44.5 million of the original $405.3
            million allocation was approved for deployment, and 1,735 eligible
            locations remain unfunded after provider withdrawals. The state has
            reopened bidding, and more than $340 million in unallocated BEAD
            funds awaits federal guidance on permissible uses. PADD’s role is
            ongoing advocacy and gap-fill monitoring: tracking the NBO’s
            rebidding process, ensuring Panhandle communities are represented in
            subsequent funding rounds, and connecting ISPs and local governments
            to supplemental programs (USDA ReConnect, FCC E-Rate, state
            broadband grants) where BEAD coverage falls short.
          </li>
          <li>
            <strong>Water and Wastewater:</strong> Aging small-system
            infrastructure is a persistent challenge. Systematic prioritization
            plus proactive pursuit of USDA Rural Development and CWSRF funding
            should be a near-term priority.
          </li>
          <li>
            <strong>Energy:</strong> Transmission constraints are the primary
            barrier to renewable development; federal transmission investment
            could unlock substantial wind and solar potential.
          </li>
          <li>
            <strong>Healthcare:</strong> Facility viability is at risk from
            provider shortages and the financial fragility of small CAHs; HPSA
            designations should be actively leveraged.
          </li>
          <li>
            <strong>Housing:</strong> Workforce housing shortage is a binding
            constraint; a regional strategy addressing both new construction and
            rehabilitation is needed.
          </li>
        </ul>
      </div>
    </div>
  );
}
